import Toggle from 'react-toggle';
import type { FunctionComponent } from 'react';

import { usePlanSelectorContext } from '../../../../contexts/PlanSelectorContext';
import { withTargetChecked } from '../../../../utils';
import { Select } from '../../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, LoaderButton } from '../../../generic/Button';
import { FormLabel } from '../../../generic/Forms';
import Icon, { icons, IconText } from '../../../generic/Icon';
import Tooltip from '../../../generic/Tooltip';
import type { SelectOption } from '../../../../types';

import JiraSettingsSection from './JiraSettingsSection';

export type JiraSettingsSiteSelectionProps = {
  sites: Array<SelectOption<number>>; // { label: 'Some Jira', value: 1 }, where value is the ID of that Jira
  selectedOption: number | null;
  isSiteEnabled: boolean;
  isDeletingSite: boolean;
  onSiteSelect: (jiraId?: number) => void;
  onDeleteSite: () => void;
  onToggleEnabled: (isEnabled: boolean) => void;
  onClickAddSite: () => void;
  isPaidRestricted: boolean;
};

type Props = JiraSettingsSiteSelectionProps;

/**
 * Handles switching between various Jira setups,
 * as well as housing some site-specific settings.
 */
const JiraSettingsSiteSelection: FunctionComponent<Props> = ({
  sites,
  selectedOption,
  isSiteEnabled,
  isDeletingSite,
  onSiteSelect,
  onDeleteSite,
  onToggleEnabled,
  onClickAddSite,
  isPaidRestricted,
}) => {
  const { showPlanSelectorModal } = usePlanSelectorContext();

  return (
    <JiraSettingsSection heading="Site">
      <div className="jira-settings__site-selection">
        <div className="jira-settings__site-selection--top">
          <div className="jira-settings__site-selection__selector">
            <Select
              id="jira-site-select"
              options={sites}
              value={sites.find(site => site.value === selectedOption)}
              onChange={(option: SelectOption<number> | null) => onSiteSelect(option?.value)}
              isDisabled={isDeletingSite}
            />
          </div>
          <ButtonGroup>
            <LoaderButton
              type="error"
              size={BUTTON_SIZE.MEDIUM}
              onClick={onDeleteSite}
              isDisabled={isDeletingSite}
              isLoading={isDeletingSite}
            >
              <IconText
                icon={icons.trash}
                text="Delete Site"
              />
            </LoaderButton>
            <Button
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={() => {
                if (isPaidRestricted) {
                  showPlanSelectorModal({ focusFeature: 'jiraIntegrationFeature' });
                  return;
                }

                onClickAddSite();
              }}
            >
              <IconText
                icon={isPaidRestricted ? icons.lock : icons.plus}
                text="Add Site"
              />
            </Button>
          </ButtonGroup>
        </div>
        <div className="jira-settings__site-selection--bottom">
          <FormLabel
            label="Enabled"
            htmlFor="enabled"
            isInline
          >
            <Toggle
              id="enabled"
              checked={isSiteEnabled}
              onChange={withTargetChecked(onToggleEnabled)}
              icons={false}
              disabled={isPaidRestricted}
            />
          </FormLabel>
          <Tooltip
            id="jira-enabled-tooltip"
            tooltip="Specify if this Jira instance should be shown when exporting an issue."
            placement="right"
          >
            <span className="inline-block margin-left-xs">
              <Icon icon={icons.infoCircle} />
            </span>
          </Tooltip>
        </div>
      </div>
    </JiraSettingsSection>
  );
};

export default JiraSettingsSiteSelection;
